// src/pages/SchedulerPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { add, isBefore } from 'date-fns';
import { RRule } from 'rrule'; // For expanding recurrences
import { api } from '../scripts/api';
import { Truck, Wrench, CalendarDays } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Styles

const locales = { 'en-US': require('date-fns/locale/en-US') };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const eventTypes = {
  service: { color: '#FF9800', icon: Wrench },
  rental: { color: '#4CAF50', icon: Truck },
  maintenance: { color: '#2196F3', icon: CalendarDays },
  // Add more
};

export default function SchedulerPage() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null); // For modal/edit

  useEffect(() => {
    fetchEvents();
  }, [date, view]);

  const fetchEvents = async () => {
    const start = new Date(date);
    const end = add(start, { months: view === 'month' ? 1 : 0, weeks: view === 'week' ? 1 : 0, days: view === 'day' ? 1 : 0 });
    try {
      const { data } = await api.get('/schedules', {
        params: { start: start.toISOString(), end: end.toISOString() },
      });
      const expanded = expandRecurringEvents(data);
      setEvents(expanded.map(ev => ({
        ...ev,
        start: new Date(ev.start_time),
        end: new Date(ev.end_time),
        allDay: !!ev.all_day,
      })));
    } catch (e) {
      console.error('Failed to load schedules', e);
    }
  };

  // Expand RRULE to instances within view
  const expandRecurringEvents = (rawEvents) => {
    const allEvents = [];
    rawEvents.forEach(ev => {
      if (!ev.recurrence) {
        allEvents.push(ev);
        return;
      }
      try {
        const rule = RRule.fromString(ev.recurrence);
        const instances = rule.between(new Date(date), add(new Date(date), { months: 2 })); // Buffer
        instances.forEach(inst => {
          const instStart = add(new Date(ev.start_time), { milliseconds: inst - new Date(ev.start_time) });
          const duration = new Date(ev.end_time) - new Date(ev.start_time);
          allEvents.push({ ...ev, start_time: instStart.toISOString(), end_time: add(instStart, { milliseconds: duration }).toISOString(), isInstance: true });
        });
      } catch {} // Skip invalid
    });
    return allEvents;
  };

  const eventStyleGetter = (event) => {
    const typeInfo = eventTypes[event.type] || { color: '#757575' };
    return { style: { backgroundColor: typeInfo.color } };
  };

  // Placeholder modal for create/edit (expand with form)
  const handleSelectSlot = ({ start, end }) => {
    // Open modal to create new event
    alert(`Create event from ${start} to ${end}`);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    // Open edit modal
  };

  return (
    <div className="container">
      <h1>Scheduler</h1>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 600 }}
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        eventPropGetter={eventStyleGetter}
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        views={['month', 'week', 'day', 'agenda']}
      />
      {/* Add modal component for selectedEvent */}
    </div>
  );
}